/**
 * Tidal levels — MHWS, MHWN, MLWN, and MLWS, the four numbers printed in the
 * tidal-levels box on a chart and at the head of every almanac port page. They
 * are the means of the high and low waters at springs and at neaps, and they're
 * the reference a navigator carries in their head: "it's roughly MHWN today, so
 * about so-much of tide". From them fall the spring and neap RANGES and the mean
 * level, which is what the height curves and the charted drying heights hang off.
 *
 * We DERIVE them from the run of predicted high and low waters we already hold
 * for each port (the RYA training height dataset), the same way the published
 * figures are themselves derived from a long run of predictions:
 *   - work out the RANGE of each tidal cycle (how far the water rose or fell);
 *   - call the biggest-range cycles springs and the smallest-range cycles neaps;
 *   - average the high waters and the low waters within each group.
 * Springs and neaps are split at the upper and lower quartiles of the range, so
 * the classification comes from the data's own spread rather than a fixed guess.
 * Training data; not for real-world navigation.
 */
import { HEIGHT_PORTS, type HeightEvent, type HeightPort } from './heights';

export interface TidalLevels {
  /** Mean High Water Springs, metres above chart datum. */
  mhwsM: number;
  /** Mean High Water Neaps. */
  mhwnM: number;
  /** Mean Low Water Neaps. */
  mlwnM: number;
  /** Mean Low Water Springs. */
  mlwsM: number;
  /** Mean tide level (midway between the spring high and low means). */
  meanLevelM: number;
  /** Mean spring range, MHWS − MLWS. */
  springRangeM: number;
  /** Mean neap range, MHWN − MLWN. */
  neapRangeM: number;
  /** How many events fed the derivation, for honesty about the sample. */
  sampleSize: number;
}

/** A simple linear-interpolated percentile of a numeric array (0..1). */
function percentile(sortedAsc: number[], p: number): number {
  if (!sortedAsc.length) return NaN;
  if (sortedAsc.length === 1) return sortedAsc[0];
  const idx = p * (sortedAsc.length - 1);
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sortedAsc[lo];
  return sortedAsc[lo] + (sortedAsc[hi] - sortedAsc[lo]) * (idx - lo);
}

function mean(xs: number[]): number {
  return xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : NaN;
}

/** The range a HW or LW belongs to: its height against the mean of its neighbours of the opposite type. */
function cycleRange(events: HeightEvent[], i: number): number | null {
  const e = events[i];
  // Walk out to the nearest opposite-type events on each side.
  let prevOpp: HeightEvent | undefined;
  for (let j = i - 1; j >= 0; j--) {
    if (events[j].type !== e.type) {
      prevOpp = events[j];
      break;
    }
  }
  let nextOpp: HeightEvent | undefined;
  for (let j = i + 1; j < events.length; j++) {
    if (events[j].type !== e.type) {
      nextOpp = events[j];
      break;
    }
  }
  const opp = [prevOpp, nextOpp].filter((x): x is HeightEvent => x != null);
  if (!opp.length) return null;
  const oppMean = mean(opp.map((o) => o.heightM));
  return Math.abs(e.heightM - oppMean);
}

const round2 = (x: number) => Math.round(x * 100) / 100;

/**
 * Derive the four tidal levels (and the ranges and mean level) from a run of
 * high/low water events. Needs a handful of each; returns NaN-filled levels with
 * sampleSize 0 if there isn't enough to classify.
 */
export function deriveTidalLevels(events: HeightEvent[]): TidalLevels {
  const evs = [...events].filter((e) => Number.isFinite(e.heightM)).sort((a, b) => a.t - b.t);
  const ranges: { i: number; range: number }[] = [];
  for (let i = 0; i < evs.length; i++) {
    const r = cycleRange(evs, i);
    if (r != null) ranges.push({ i, range: r });
  }

  if (ranges.length < 4) {
    return {
      mhwsM: NaN,
      mhwnM: NaN,
      mlwnM: NaN,
      mlwsM: NaN,
      meanLevelM: NaN,
      springRangeM: NaN,
      neapRangeM: NaN,
      sampleSize: 0,
    };
  }

  const sortedRanges = ranges.map((r) => r.range).sort((a, b) => a - b);
  const springThresh = percentile(sortedRanges, 0.75);
  const neapThresh = percentile(sortedRanges, 0.25);

  const springHW: number[] = [];
  const neapHW: number[] = [];
  const springLW: number[] = [];
  const neapLW: number[] = [];
  for (const { i, range } of ranges) {
    const e = evs[i];
    const isSpring = range >= springThresh;
    const isNeap = range <= neapThresh;
    if (e.type === 'H') {
      if (isSpring) springHW.push(e.heightM);
      if (isNeap) neapHW.push(e.heightM);
    } else {
      if (isSpring) springLW.push(e.heightM);
      if (isNeap) neapLW.push(e.heightM);
    }
  }

  const mhwsM = mean(springHW);
  const mhwnM = mean(neapHW);
  const mlwnM = mean(neapLW);
  const mlwsM = mean(springLW);

  return {
    mhwsM: round2(mhwsM),
    mhwnM: round2(mhwnM),
    mlwnM: round2(mlwnM),
    mlwsM: round2(mlwsM),
    meanLevelM: round2((mhwsM + mlwsM) / 2),
    springRangeM: round2(mhwsM - mlwsM),
    neapRangeM: round2(mhwnM - mlwnM),
    sampleSize: ranges.length,
  };
}

/** Tidal levels for a height port (by its events). */
export function tidalLevelsForPort(port: HeightPort): TidalLevels {
  return deriveTidalLevels(port.events);
}

/** Tidal levels for a port id, or null if the port isn't known. */
export function tidalLevelsForPortId(id: string): TidalLevels | null {
  const port = HEIGHT_PORTS.find((p) => p.id === id);
  return port ? tidalLevelsForPort(port) : null;
}

/** A one-line plain-English read of the levels. */
export function describeTidalLevels(l: TidalLevels): string {
  if (!l.sampleSize) return 'Not enough tidal data to derive levels.';
  return (
    `MHWS ${l.mhwsM.toFixed(1)} m, MHWN ${l.mhwnM.toFixed(1)} m, ` +
    `MLWN ${l.mlwnM.toFixed(1)} m, MLWS ${l.mlwsM.toFixed(1)} m ` +
    `(spring range ${l.springRangeM.toFixed(1)} m, neap range ${l.neapRangeM.toFixed(1)} m).`
  );
}
